/**
 * External Imports
 */
import { FC, useEffect, useLayoutEffect, useState } from 'react';
import { connect, useDispatch } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

/**
 * Internal Imports
 */
import EmployeeProfileHeader from '../../components/profileHeaders/employeeProfileHeader';
import Round from '../../components/round/Round';
import Loading from '../../components/shared/Loading';
import HistorySideNav from '../../components/sideNav/historySideNav';
import HistorySideDrawer from '../../components/sideNav/historySideDrawer';

import { RootState } from '../../redux/store';
import {
  clearEmployeeRoundHistory,
  getEmployeeRoundHistory
} from '../../redux/actions';
import {
  useGetEmployeeNotesQuery,
  useGetEmployeeToRoundQuery
} from '../../redux/apis/Employees.api';

/**
 * Global Type Definition Imports
 */
import { RoundHistory } from '../../config/interfaces';

/**
 * Style Imports
 */
import { ProfileContainer } from './styles/Validation.styles';

interface ValidationProfileProps {
  employeeRoundHistory?: RoundHistory[];
}

const ValidationProfile: FC<ValidationProfileProps> = ({
  employeeRoundHistory
}) => {
  const dispatch = useDispatch();

  const [selectedTab, setSelectedTab] = useState<string>('');

  const { state } = useLocation();
  const { validationType = '' } = useParams();
  const roundType =
    validationType === 'aidet'
      ? 'aidetValidation'
      : validationType === 'bssr'
      ? 'bssrValidation'
      : validationType === 'safety'
      ? 'safetyValidation'
      : 'validation';
  const navigate = useNavigate();
  const { employeeId = '' } =
    (state as {
      employeeId: string;
    }) || {};

  const {
    data: employeeToRoundData,
    // isError: isGetEmployeeToRoundError,
    isFetching: isGetEmployeeToRoundFetching
    // isLoading: isGetEmployeeToRoundLoading
  } = useGetEmployeeToRoundQuery(employeeId, {
    refetchOnMountOrArgChange: true,
    skip: !employeeId
  });
  const {
    data: employeeNotesData,
    // isError: isGetEmployeeNotesError,
    // isFetching: isGetEmployeeNotesFetching
    isLoading: isGetEmployeeNotesLoading
  } = useGetEmployeeNotesQuery(employeeId, {
    pollingInterval: 60000,
    refetchOnMountOrArgChange: true,
    skip: !employeeToRoundData?.employeeId
  });

  useLayoutEffect(() => {
    if (employeeToRoundData) {
      dispatch(
        getEmployeeRoundHistory({
          employeeId: employeeToRoundData.hcaid,
          roundingTypes: [roundType]
        })
      );
    } else if (!employeeId) {
      // TODO: Hack to return to patient census when refreshing or navigating straight to profile
      navigate(window.localStorage.getItem('orbitLastPath') ?? '/');
    }
  }, [employeeToRoundData]);

  useEffect(() => {
    return () => {
      dispatch(clearEmployeeRoundHistory());
    };
  }, []);

  // TODO: Do we need to have historic panel open by default for validation rounds?
  // useEffect(() => {
  //   const roundCount = employeeRoundHistory?.length ?? 0;
  //   if (roundCount > 0) {
  //     setSelectedTab('pastRounds');
  //   }
  // }, [employeeRoundHistory]);

  return (
    <ProfileContainer
      className="neu-bg-plain-0"
      data-testid="validation-profile-view"
    >
      {isGetEmployeeToRoundFetching || isGetEmployeeNotesLoading ? (
        <Loading centered label="subject data..." />
      ) : (
        <>
          <section>
            <EmployeeProfileHeader selectedEmployee={employeeToRoundData} />
          </section>
          <section className="d-flex" style={{ height: 'calc(100vh - 163px)' }}>
            <HistorySideNav
              selectedEmployee={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              type="validation"
              supportType={validationType}
            />
            <HistorySideDrawer
              selectedTab={selectedTab}
              selectedPerson={{
                ...employeeToRoundData,
                notes: employeeNotesData || []
              }}
              roundHistory={employeeRoundHistory}
              tasks={[]}
              type="validation"
              supportType={validationType}
              setSelectedTab={setSelectedTab}
            />
            <Round type={roundType} selectedEmployee={employeeToRoundData} />
          </section>
        </>
      )}
    </ProfileContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    employeeRoundHistory: state.ReportReducer.employeeRoundHistory,
    issues: state.TaskReducer.employeeIssues,
    feedback: state.TaskReducer.employeeFeedback
  };
};

export default connect(mapReduxStateToProps)(ValidationProfile);
